import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
    ChevronLeft, ChevronRight, RefreshCw, CheckCircle2, Loader2, Download,
    FolderOpen, ArrowLeft,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmt = (n) => `$${(Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * WeeklySettlementPanel — admin master view.
 *
 *  • Default mode: compact list of operator "folders" (name, distributor,
 *    net house, settled badge). No per-operator details inline — keeps the
 *    dashboard tidy.
 *  • Drill mode: click any operator → opens that operator's focused folder
 *    with full activity tiles, per-cashier breakdown, and the Mark Settled
 *    workflow. A back arrow returns to the master list.
 */
const WeeklySettlementPanel = () => {
    const [cycle, setCycle] = useState('current');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [drillId, setDrillId] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/admin/weekly-settlement`, {
                params: { cycle }, withCredentials: true,
            });
            setData(data);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to load weekly settlement');
        } finally {
            setLoading(false);
        }
    }, [cycle]);

    useEffect(() => { load(); }, [load]);

    const exportCsv = () => {
        if (!data) return;
        const rows = [
            ['Cycle', 'Distributor', 'Operator', 'Players', 'Loads', 'Cashouts', 'Loaded', 'Cashed', 'Wagered', 'Won', 'Net House', 'Settled', 'Op %', 'House %', 'Note'],
            ...data.rows.map(r => [
                data.cycle, r.distributor_name || '', r.operator_name,
                r.totals.player_count, r.totals.load_count, r.totals.cashout_count,
                r.totals.loaded.toFixed(2), r.totals.cashed.toFixed(2),
                r.totals.wagered.toFixed(2), r.totals.won.toFixed(2),
                r.totals.net_house.toFixed(2),
                r.settlement.settled ? 'YES' : 'NO',
                r.settlement.split_operator_pct ?? '',
                r.settlement.split_house_pct ?? '',
                r.settlement.note ?? '',
            ]),
        ];
        const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `weekly_settlement_${data.cycle}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const formatRange = () => {
        if (!data) return '';
        const start = new Date(data.cycle_start);
        const end = new Date(data.cycle_end);
        const opts = { month: 'short', day: 'numeric' };
        return `${start.toLocaleDateString('en-US', opts)} → ${end.toLocaleDateString('en-US', opts)} (CT)`;
    };

    if (drillId) {
        return (
            <OperatorDrillCard
                operatorId={drillId}
                cycle={cycle}
                onBack={() => setDrillId(null)}
                onSettled={load}
            />
        );
    }

    return (
        <div className="rounded-md border border-zinc-800 bg-zinc-900/30" data-testid="weekly-settlement-panel">
            <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 border-b border-zinc-800">
                <div>
                    <h2 className="text-sm font-semibold text-zinc-100">Weekly settlement</h2>
                    <p className="text-xs text-zinc-500 mt-0.5">
                        Mon → Sun (America/Chicago). Click any operator to open their folder.
                        {data && <> · {data.cycle} · {formatRange()}</>}
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <Tabs value={cycle} onValueChange={setCycle}>
                        <TabsList className="bg-zinc-950 border border-zinc-800 h-8">
                            <TabsTrigger value="prev" className="text-xs data-[state=active]:bg-zinc-800 data-[state=active]:text-zinc-100" data-testid="cycle-prev">
                                <ChevronLeft className="w-3 h-3 mr-1" />Last week
                            </TabsTrigger>
                            <TabsTrigger value="current" className="text-xs data-[state=active]:bg-zinc-800 data-[state=active]:text-zinc-100" data-testid="cycle-current">
                                This week
                            </TabsTrigger>
                        </TabsList>
                    </Tabs>
                    <Button onClick={load} size="sm" variant="outline"
                        className="h-8 border-zinc-800 bg-zinc-900 text-zinc-300 hover:bg-zinc-800"
                        data-testid="reload-settlement">
                        <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
                    </Button>
                    <Button onClick={exportCsv} size="sm" variant="outline"
                        className="h-8 border-zinc-800 bg-zinc-900 text-zinc-300 hover:bg-zinc-800"
                        data-testid="export-csv">
                        <Download className="w-3.5 h-3.5 mr-1.5" /> CSV
                    </Button>
                </div>
            </div>

            {/* Compact roll-up strip — five small tiles */}
            {data && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 p-5 border-b border-zinc-800">
                    <RollTile label="Loaded" value={fmt(data.rollup.loaded)} />
                    <RollTile label="Cashed out" value={fmt(data.rollup.cashed)} />
                    <RollTile
                        label="Net house"
                        value={fmt(data.rollup.net_house)}
                        valueClass={data.rollup.net_house >= 0 ? 'text-emerald-300' : 'text-rose-300'}
                    />
                    <RollTile label="Operators" value={`${data.rollup.settled_count} / ${data.rollup.operator_count}`} sub="settled" />
                    <RollTile label="Cycle" value={data.cycle} sub={formatRange()} />
                </div>
            )}

            {/* Operator folder list — minimal columns only */}
            <div className="overflow-x-auto">
                <Table>
                    <TableHeader>
                        <TableRow className="border-zinc-800 hover:bg-transparent">
                            <TableHead className="text-[11px] uppercase tracking-wider text-zinc-500 font-medium">Operator</TableHead>
                            <TableHead className="text-[11px] uppercase tracking-wider text-zinc-500 font-medium">Distributor</TableHead>
                            <TableHead className="text-[11px] uppercase tracking-wider text-zinc-500 font-medium text-right">Net house</TableHead>
                            <TableHead className="text-[11px] uppercase tracking-wider text-zinc-500 font-medium">Status</TableHead>
                            <TableHead className="text-[11px] uppercase tracking-wider text-zinc-500 font-medium text-right">Open</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {!data || data.rows.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={5} className="text-center text-zinc-500 py-10 text-sm">
                                    No operator activity this cycle.
                                </TableCell>
                            </TableRow>
                        ) : data.rows.map((r, i) => (
                            <TableRow
                                key={r.operator_id}
                                className="border-zinc-800 hover:bg-zinc-900/60 cursor-pointer"
                                onClick={() => setDrillId(r.operator_id)}
                                data-testid={`settle-row-${i}`}
                            >
                                <TableCell className="text-zinc-100 font-medium">
                                    <div className="flex items-center gap-2">
                                        <FolderOpen className="w-4 h-4 text-zinc-500" />
                                        {r.operator_name}
                                    </div>
                                </TableCell>
                                <TableCell className="text-zinc-400 text-sm">
                                    {r.distributor_name || <span className="text-zinc-600">—</span>}
                                </TableCell>
                                <TableCell className={`text-right font-mono font-semibold ${r.totals.net_house >= 0 ? 'text-emerald-300' : 'text-rose-300'}`}>
                                    {fmt(r.totals.net_house)}
                                </TableCell>
                                <TableCell>
                                    {r.settlement.settled ? (
                                        <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-widest text-emerald-300 bg-emerald-500/10 border border-emerald-500/20 px-2 py-0.5 rounded">
                                            <CheckCircle2 className="w-3 h-3" /> Settled
                                        </span>
                                    ) : (
                                        <span className="text-[11px] uppercase tracking-widest text-zinc-400">
                                            Open
                                        </span>
                                    )}
                                </TableCell>
                                <TableCell className="text-right">
                                    <ChevronRight className="w-4 h-4 text-zinc-500 inline-block" />
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>
        </div>
    );
};

// ───────── Drill-down: one operator's folder ─────────

const OperatorDrillCard = ({ operatorId, cycle, onBack, onSettled }) => {
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(true);
    const [settleOpen, setSettleOpen] = useState(false);
    const [splitOpPct, setSplitOpPct] = useState('50');
    const [splitHousePct, setSplitHousePct] = useState('50');
    const [settleNote, setSettleNote] = useState('');
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/admin/weekly-settlement/operator/${operatorId}`, {
                params: { cycle }, withCredentials: true,
            });
            setDetail(data);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to load operator');
        } finally {
            setLoading(false);
        }
    }, [operatorId, cycle]);

    useEffect(() => { load(); }, [load]);

    const openSettle = () => {
        if (!detail) return;
        setSplitOpPct('50');
        setSplitHousePct('50');
        setSettleNote('');
        setSettleOpen(true);
    };

    const submitSettle = async () => {
        const op = parseFloat(splitOpPct);
        const ho = parseFloat(splitHousePct);
        if (isNaN(op) || isNaN(ho) || Math.abs((op + ho) - 100) > 0.01) {
            toast.error('Operator + House percentages must sum to 100');
            return;
        }
        setBusy(true);
        try {
            await axios.post(`${API}/admin/weekly-settlement/mark-settled`, {
                operator_id: detail.operator_id,
                cycle: detail.cycle,
                split_operator_pct: op,
                split_house_pct: ho,
                note: settleNote.trim() || undefined,
            }, { withCredentials: true });
            toast.success(`Settled ${detail.operator_name} for ${detail.cycle}`);
            setSettleOpen(false);
            await load();
            onSettled?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Settle failed');
        } finally {
            setBusy(false);
        }
    };

    const unsettle = async () => {
        if (!detail) return;
        if (!window.confirm(`Unsettle ${detail.operator_name} for ${detail.cycle}?`)) return;
        try {
            await axios.post(`${API}/admin/weekly-settlement/unsettle`, {
                operator_id: detail.operator_id, cycle: detail.cycle,
            }, { withCredentials: true });
            toast.success('Settlement cleared');
            await load();
            onSettled?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed');
        }
    };

    if (loading || !detail) {
        return (
            <div className="rounded-md border border-zinc-800 bg-zinc-900/30 p-10 text-center text-zinc-500">
                <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" /> Loading operator…
            </div>
        );
    }

    const t = detail.totals;
    const settled = detail.settlement?.settled;
    const start = new Date(detail.cycle_start);
    const end = new Date(detail.cycle_end);
    const opts = { month: 'short', day: 'numeric' };
    const range = `${start.toLocaleDateString('en-US', opts)} → ${end.toLocaleDateString('en-US', opts)} (CT)`;
    const houseNeg = t.net_house < 0;

    return (
        <div className="rounded-md border border-zinc-800 bg-zinc-900/30" data-testid="operator-drill-card">
            <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 border-b border-zinc-800">
                <div className="flex items-center gap-3">
                    <Button onClick={onBack} size="sm" variant="outline"
                        className="h-8 border-zinc-800 bg-zinc-900 text-zinc-300 hover:bg-zinc-800"
                        data-testid="drill-back">
                        <ArrowLeft className="w-3.5 h-3.5 mr-1.5" /> All operators
                    </Button>
                    <div>
                        <div className="flex items-center gap-2">
                            <FolderOpen className="w-4 h-4 text-zinc-400" />
                            <h2 className="text-sm font-semibold text-zinc-100">{detail.operator_name}</h2>
                            {settled && (
                                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-emerald-300 bg-emerald-500/10 border border-emerald-500/20 px-2 py-0.5 rounded">
                                    <CheckCircle2 className="w-3 h-3" /> Settled
                                </span>
                            )}
                        </div>
                        <p className="text-xs text-zinc-500 mt-0.5">
                            {detail.distributor_name ? `${detail.distributor_name} · ` : ''}
                            {detail.cycle} · {range}
                            {detail.operator_phone ? ` · ${detail.operator_phone}` : ''}
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {settled ? (
                        <Button onClick={unsettle} size="sm" variant="outline"
                            className="h-8 border-zinc-800 bg-zinc-900 text-zinc-300 hover:bg-zinc-800"
                            data-testid="unsettle-operator">
                            Unsettle
                        </Button>
                    ) : (
                        <Button onClick={openSettle} size="sm"
                            className="h-8 bg-zinc-100 text-zinc-900 hover:bg-white"
                            data-testid="open-settle">
                            Mark settled
                        </Button>
                    )}
                </div>
            </div>

            <div className="p-5 space-y-4">
                {/* Activity tiles */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <DrillTile label="Loaded" value={fmt(t.loaded)} sub={`${t.load_count} txns`} />
                    <DrillTile label="Cashed out" value={fmt(t.cashed)} sub={`${t.cashout_count} txns`} />
                    <DrillTile label="Wagered" value={fmt(t.wagered)} />
                    <DrillTile label="Won" value={fmt(t.won)} />
                </div>

                {/* Net house emphasis */}
                <div className={`rounded-md border px-5 py-4 ${houseNeg ? 'border-rose-900/50 bg-rose-950/20' : 'border-emerald-900/50 bg-emerald-950/20'}`}>
                    <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-medium">Net house (this week)</p>
                    <p className={`text-3xl font-mono font-semibold mt-1 ${houseNeg ? 'text-rose-300' : 'text-emerald-300'}`} data-testid="drill-net-house">
                        {fmt(t.net_house)}
                    </p>
                    <p className="text-xs text-zinc-500 mt-1">
                        {settled && detail.settlement?.split_house_pct != null
                            ? `Settled at House ${detail.settlement.split_house_pct}% / Operator ${detail.settlement.split_operator_pct}%`
                            : 'Loaded − Cashed out. Reconciled with the operator manually.'}
                        {detail.settlement?.note ? ` — ${detail.settlement.note}` : ''}
                    </p>
                </div>

                {/* Per-cashier breakdown */}
                <div>
                    <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-medium mb-2">Cashiers — this cycle</p>
                    {(!detail.by_cashier || detail.by_cashier.length === 0) ? (
                        <p className="text-sm text-zinc-500 bg-zinc-950 border border-zinc-800 rounded-md px-3 py-3">
                            No cashier activity this cycle.
                        </p>
                    ) : (
                        <div className="rounded-md border border-zinc-800 overflow-hidden">
                            <Table>
                                <TableHeader>
                                    <TableRow className="border-zinc-800 hover:bg-transparent bg-zinc-950">
                                        <TableHead className="text-[11px] uppercase tracking-wider text-zinc-500 font-medium">Cashier</TableHead>
                                        <TableHead className="text-[11px] uppercase tracking-wider text-zinc-500 font-medium text-right">Loads</TableHead>
                                        <TableHead className="text-[11px] uppercase tracking-wider text-zinc-500 font-medium text-right">Loaded</TableHead>
                                        <TableHead className="text-[11px] uppercase tracking-wider text-zinc-500 font-medium text-right">Cashouts</TableHead>
                                        <TableHead className="text-[11px] uppercase tracking-wider text-zinc-500 font-medium text-right">Cashed</TableHead>
                                        <TableHead className="text-[11px] uppercase tracking-wider text-zinc-500 font-medium text-right">Net</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {detail.by_cashier.map((c, i) => (
                                        <TableRow key={c.cashier_id || `direct-${i}`} className="border-zinc-800 hover:bg-zinc-900/60" data-testid={`cashier-row-${i}`}>
                                            <TableCell className="text-zinc-100 font-medium">{c.cashier_name}</TableCell>
                                            <TableCell className="text-right text-zinc-400 font-mono text-sm">{c.load_count}</TableCell>
                                            <TableCell className="text-right text-zinc-100 font-mono">{fmt(c.loaded)}</TableCell>
                                            <TableCell className="text-right text-zinc-400 font-mono text-sm">{c.cashout_count}</TableCell>
                                            <TableCell className="text-right text-zinc-100 font-mono">{fmt(c.cashed)}</TableCell>
                                            <TableCell className={`text-right font-mono font-semibold ${c.net >= 0 ? 'text-emerald-300' : 'text-rose-300'}`}>
                                                {fmt(c.net)}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </div>
            </div>

            {/* Settle dialog */}
            <Dialog open={settleOpen} onOpenChange={setSettleOpen}>
                <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100" data-testid="settle-dialog">
                    <DialogHeader>
                        <DialogTitle className="text-zinc-100">Settle {detail.operator_name}</DialogTitle>
                        <DialogDescription className="text-zinc-400">
                            Net house this week:{' '}
                            <span className={`font-semibold font-mono ${houseNeg ? 'text-rose-300' : 'text-emerald-300'}`}>{fmt(t.net_house)}</span>.
                            {t.net_house > 0 && (
                                <span className="block mt-1 text-xs">
                                    At 50/50 you keep <strong className="text-zinc-100">{fmt(t.net_house / 2)}</strong> and pay operator <strong className="text-zinc-100">{fmt(t.net_house / 2)}</strong>.
                                </span>
                            )}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="grid grid-cols-2 gap-3 py-2">
                        <div>
                            <Label className="text-zinc-300 text-xs">House %</Label>
                            <Input type="number" min="0" max="100" step="1" value={splitHousePct}
                                onChange={(e) => { setSplitHousePct(e.target.value); const v = parseFloat(e.target.value); if (!isNaN(v)) setSplitOpPct(String(100 - v)); }}
                                className="mt-1 bg-zinc-950 border-zinc-800 text-zinc-100 font-mono"
                                data-testid="split-house-pct" />
                        </div>
                        <div>
                            <Label className="text-zinc-300 text-xs">Operator %</Label>
                            <Input type="number" min="0" max="100" step="1" value={splitOpPct}
                                onChange={(e) => { setSplitOpPct(e.target.value); const v = parseFloat(e.target.value); if (!isNaN(v)) setSplitHousePct(String(100 - v)); }}
                                className="mt-1 bg-zinc-950 border-zinc-800 text-zinc-100 font-mono"
                                data-testid="split-op-pct" />
                        </div>
                    </div>
                    <div>
                        <Label className="text-zinc-300 text-xs">Note (optional)</Label>
                        <Input value={settleNote} onChange={(e) => setSettleNote(e.target.value)}
                            placeholder="e.g. paid $300 cash on Mon"
                            className="mt-1 bg-zinc-950 border-zinc-800 text-zinc-100"
                            data-testid="settle-note" />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setSettleOpen(false)}
                            className="border-zinc-800 bg-zinc-900 text-zinc-300 hover:bg-zinc-800">Cancel</Button>
                        <Button onClick={submitSettle} disabled={busy}
                            className="bg-zinc-100 text-zinc-900 hover:bg-white" data-testid="confirm-settle">
                            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Mark settled'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

const RollTile = ({ label, value, sub, valueClass = 'text-zinc-100' }) => (
    <div className="rounded-md border border-zinc-800 bg-zinc-950 px-3 py-2.5">
        <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-medium">{label}</p>
        <p className={`text-base font-mono font-semibold ${valueClass}`}>{value}</p>
        {sub && <p className="text-[10px] text-zinc-500 mt-0.5">{sub}</p>}
    </div>
);

const DrillTile = ({ label, value, sub }) => (
    <div className="rounded-md border border-zinc-800 bg-zinc-950 px-4 py-3">
        <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-medium">{label}</p>
        <p className="text-xl font-mono font-semibold text-zinc-100 mt-1">{value}</p>
        {sub && <p className="text-[10px] text-zinc-500 mt-0.5">{sub}</p>}
    </div>
);

export default WeeklySettlementPanel;
